import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { ChevronDown, InfoCircleFill } from 'react-bootstrap-icons';

import apiClient, { API_ENDPOINTS, LLMModelInfo, LLMProviderStatusItem, LLMRouteConfigItem, getLLMConfig, getLLMModels, getLLMRoutes, updateLLMConfig, updateLLMRoutes, OrgPromptInstructionItem, getOrgPromptInstructions, updateOrgPromptInstructions, getVectorIngestionKeyStatus, updateVectorIngestionKey, getGlobalBillingSettings, updateGlobalBillingSettings } from '../api/config';
import CustomDropdown, { CustomDropdownOption } from '../components/CustomDropdown';
import { useAuth } from '../contexts/AuthContext';

import './AdminConfig-new.css';

/* ============================================================
   Types
   ============================================================ */

interface PromptConfig
{
    agent_name: string;
    content: string;
    description?: string;
    type: string;
}

interface LLMConfig {
    subscription_mode: 'platform' | 'byo_key';
    provider: string;
    model: string;
    temperature: number;
    reasoning_effort: string;
}

interface ProviderKeyFormState {
    openai: string;
    gemini: string;
    anthropic: string;
}

interface ProviderKeyTouchedState {
    openai: boolean;
    gemini: boolean;
    anthropic: boolean;
}

interface LLMRouteFormItem {
    route_key: string;
    label: string;
    description?: string;
    enabled: boolean;
    is_inherited?: boolean;
    inherited_from_route?: string | null;
    provider: string;
    model: string;
    temperature: number;
    reasoning_effort: string;
    effective_model: string;
}

interface LLMConfigCapabilities {
    can_configure_byo: boolean;
    can_configure_platform: boolean;
    can_edit_model_defaults: boolean;
    can_edit_route_overrides: boolean;
    can_edit_provider_keys: boolean;
    has_stored_byo_keys: boolean;
    platform_mode_locked: boolean;
    platform_mode_lock_reason: string | null;
}

/* ============================================================
   Constants
   ============================================================ */

const PROMPT_TYPE_DEFAULT = 'code_repo_prompts';
const DEFAULT_LLM_MODEL = 'openai/gpt-5.6-terra';
const DEFAULT_TEMPERATURE = 0;
const DEFAULT_REASONING_EFFORT = 'low';
const SUPPORTED_PROVIDERS = ['openai', 'gemini', 'anthropic'] as const;
const PROVIDER_OPTIONS: CustomDropdownOption[] = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'gemini', label: 'Google Gemini' },
    { value: 'anthropic', label: 'Anthropic (Claude)' },
];
const HIDDEN_MODEL_ROUTE_KEYS = new Set([
    'code_analysis',
    'unit_test_generation',
    'repository_test_case_generation',
    'code_sentinel',
]);
/* ============================================================
   Helpers
   ============================================================ */

/**
 * clearSuccess
 * Clears the success message.
 */
function clearSuccess(setSuccess: React.Dispatch<React.SetStateAction<string | null>>): void
{
    setSuccess(null);
}

/**
 * scheduleClearSuccess
 * Clears success text after a delay.
 */
function scheduleClearSuccess(setSuccess: React.Dispatch<React.SetStateAction<string | null>>): void
{
    window.setTimeout(clearSuccess.bind(null, setSuccess), 2000);
}

function formatLargeNumber(value?: number | null): string
{
    if (!value)
    {
        return '—';
    }
    if (value >= 1000000)
    {
        return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 2)}M`;
    }
    if (value >= 1000)
    {
        return `${Math.round(value / 1000)}k`;
    }
    return String(value);
}

/**
 * getModelDisplayName
 * Returns the server-provided display name when available.
 */
function getModelDisplayName(modelValue: string, availableModels: LLMModelInfo[] = []): string
{
    const normalizedModelValue = normalizeModelValue(modelValue);
    const selected = availableModels.find(item => item.id === normalizedModelValue);
    if (selected?.display_name)
    {
        return selected.display_name;
    }
    return normalizedModelValue || 'Select a model';
}

function formatModelLimits(model?: LLMModelInfo | null): string
{
    if (!model || (!model.context_window && !model.max_output))
    {
        return '';
    }

    const parts: string[] = [];
    if (model.context_window)
    {
        parts.push(`${formatLargeNumber(model.context_window)} context`);
    }
    if (model.max_output)
    {
        parts.push(`max ${formatLargeNumber(model.max_output)} output`);
    }

    return parts.join(' • ');
}

function normalizeModelValue(modelValue: string | null | undefined): string
{
    const model = String(modelValue || DEFAULT_LLM_MODEL).trim();
    if (model.startsWith('openai/responses/'))
    {
        return model.replace('openai/responses/', 'openai/');
    }
    if (model.startsWith('openai/'))
    {
        return model;
    }
    if (model.startsWith('gpt-'))
    {
        return `openai/${model}`;
    }
    if (model.startsWith('google/gemini-') || model.startsWith('vertex_ai/gemini-'))
    {
        const bare = model.split('/').slice(1).join('/');
        return `gemini/${bare}`;
    }
    if (model.startsWith('gemini-'))
    {
        return `gemini/${model}`;
    }
    if (model.startsWith('claude-'))
    {
        return `anthropic/${model}`;
    }
    return model;
}

function normalizeProviderValue(providerValue: string | null | undefined): typeof SUPPORTED_PROVIDERS[number]
{
    const normalized = String(providerValue || 'openai').trim().toLowerCase();
    if (SUPPORTED_PROVIDERS.includes(normalized as typeof SUPPORTED_PROVIDERS[number]))
    {
        return normalized as typeof SUPPORTED_PROVIDERS[number];
    }
    return 'openai';
}

function emptyProviderKeyState(): ProviderKeyFormState
{
    return {
        openai: '',
        gemini: '',
        anthropic: '',
    };
}

function providerKeyStateFromItems(items: Array<{ provider: string; api_key: string }> | undefined): ProviderKeyFormState
{
    const next = emptyProviderKeyState();
    for (const item of items || [])
    {
        const provider = normalizeProviderValue(item.provider);
        next[provider] = item.api_key || '';
    }
    return next;
}

function emptyProviderKeyTouchedState(): ProviderKeyTouchedState
{
    return {
        openai: false,
        gemini: false,
        anthropic: false,
    };
}

function providerStatusMap(items: LLMProviderStatusItem[]): Record<string, LLMProviderStatusItem>
{
    const next: Record<string, LLMProviderStatusItem> = {};
    for (const item of items)
    {
        next[normalizeProviderValue(item.provider)] = item;
    }
    return next;
}

function routeItemToForm(item: LLMRouteConfigItem, allModels: LLMModelInfo[] = []): LLMRouteFormItem
{
    const provider = normalizeProviderValue(item.provider || item.effective_provider);
    const model = normalizeModelValue(item.model || item.effective_model);
    const selectedModel = allModels.find(candidate => candidate.id === model) || null;
    return {
        route_key: item.route_key,
        label: item.label,
        description: item.description,
        enabled: Boolean(item.is_configured),
        is_inherited: item.is_inherited,
        inherited_from_route: item.inherited_from_route,
        provider,
        model,
        temperature: typeof item.temperature === 'number'
            ? clampTemperature(item.temperature)
            : clampTemperature(item.effective_temperature),
        reasoning_effort: normalizeReasoningEffort(item.reasoning_effort || item.effective_reasoning_effort, selectedModel),
        effective_model: normalizeModelValue(item.effective_model),
    };
}

function clampTemperature(value: number): number
{
    if (!Number.isFinite(value))
    {
        return 0;
    }
    if (value < 0)
    {
        return 0;
    }
    if (value > 2)
    {
        return 2;
    }
    return value;
}

function normalizeReasoningEffort(value: string | null | undefined, model?: LLMModelInfo | null): string
{
    if (!model)
    {
        return value || DEFAULT_REASONING_EFFORT;
    }

    const options = model.reasoning_effort_options || [];
    if (!model.supports_reasoning || options.length === 0)
    {
        return '';
    }
    if (value && options.includes(value))
    {
        return value;
    }
    if (model.reasoning_effort_default && options.includes(model.reasoning_effort_default))
    {
        return model.reasoning_effort_default;
    }
    return options[0] || DEFAULT_REASONING_EFFORT;
}

function canAdjustTemperature(model?: LLMModelInfo | null, reasoningEffort?: string): boolean
{
    if (!model)
    {
        return true;
    }
    if (model.supports_temperature === 'yes')
    {
        return true;
    }
    if (model.supports_temperature === 'conditional')
    {
        return (reasoningEffort || 'none') === 'none';
    }
    return false;
}

function getTemperatureSupportMessage(model?: LLMModelInfo | null, reasoningEffort?: string): string
{
    if (!model)
    {
        return '';
    }
    if (model.supports_temperature === 'yes')
    {
        return '';
    }
    if (model.supports_temperature === 'conditional')
    {
        if ((reasoningEffort || 'none') === 'none')
        {
            return 'Temperature is available when reasoning effort is set to "none".';
        }
        return 'Temperature is disabled unless reasoning effort is set to "none".';
    }
    return 'This model does not support temperature adjustment.';
}

function getUnavailableModelNotice(modelId: string, models: LLMModelInfo[]): string
{
    const normalizedModelId = normalizeModelValue(modelId);
    if (!normalizedModelId || models.some((model) => model.id === normalizedModelId))
    {
        return '';
    }
    return `The saved model '${normalizedModelId}' is no longer available in the model catalogue. Select a replacement before saving.`;
}

/**
 * buildPromptGroups
 * Groups prompts by their type key.
 */
function buildPromptGroups(prompts: PromptConfig[]): Record<string, PromptConfig[]>
{
    const groups: Record<string, PromptConfig[]> = {};
    for (const prompt of prompts)
    {
        const key = prompt.type || PROMPT_TYPE_DEFAULT;
        if (!groups[key])
        {
            groups[key] = [];
        }
        groups[key].push(prompt);
    }
    for (const key of Object.keys(groups))
    {
        groups[key].sort(comparePromptByAgentName);
    }
    return groups;
}

/**
 * comparePromptByAgentName
 * Sorts prompt configs by agent name.
 */
function comparePromptByAgentName(a: PromptConfig, b: PromptConfig): number
{
    return a.agent_name.localeCompare(b.agent_name);
}

/**
 * buildPromptTypeOrder
 * Returns type keys in a stable display order.
 */
function buildPromptTypeOrder(groups: Record<string, PromptConfig[]>): string[]
{
    const knownOrder = ['code_repo_prompts', 'requirements_agent_prompt', 'test_cases_agent_prompt'];
    const existingTypes = Object.keys(groups);
    const ordered: string[] = [];
    for (const item of knownOrder)
    {
        if (existingTypes.includes(item))
        {
            ordered.push(item);
        }
    }
    const extras = buildUnknownTypes(existingTypes, knownOrder);
    for (const extra of extras)
    {
        ordered.push(extra);
    }
    return ordered;
}

/**
 * buildUnknownTypes
 * Returns sorted type keys not in the known order list.
 */
function buildUnknownTypes(existingTypes: string[], knownOrder: string[]): string[]
{
    const extras: string[] = [];
    for (const type of existingTypes)
    {
        if (!knownOrder.includes(type))
        {
            extras.push(type);
        }
    }
    extras.sort();
    return extras;
}

/**
 * getPromptTypeLabel
 * Converts prompt type keys to display labels.
 */
function getPromptTypeLabel(typeKey: string): string
{
    let label = typeKey.replace(/_prompts?$/, '');
    const parts = label.split('_');
    const formatted: string[] = [];
    for (const word of parts)
    {
        formatted.push(word.charAt(0).toUpperCase() + word.slice(1));
    }
    label = formatted.join(' ');
    return label;
}

/**
 * loadConfig
 * Loads LLM configuration.
 */
async function loadConfig(
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
    setForm: React.Dispatch<React.SetStateAction<LLMConfig>>,
    setProviderKeys: React.Dispatch<React.SetStateAction<ProviderKeyFormState>>,
    setProviderKeyTouched: React.Dispatch<React.SetStateAction<ProviderKeyTouchedState>>,
    setProviderStatuses: React.Dispatch<React.SetStateAction<LLMProviderStatusItem[]>>,
    setInitialSubscriptionMode: React.Dispatch<React.SetStateAction<'platform' | 'byo_key'>>,
    setAvailableModels: React.Dispatch<React.SetStateAction<LLMModelInfo[]>>,
    setCapabilities: React.Dispatch<React.SetStateAction<LLMConfigCapabilities>>,
    isSuperAdmin: boolean,
): Promise<void>
{
    setLoading(true);
    setError(null);
    try
    {
        const data = await getLLMConfig();
        const provider = normalizeProviderValue(data.provider);
        const models = await getLLMModels(provider).catch(() => []);
        setAvailableModels(models);

        const resolvedModel = normalizeModelValue(data.model) || models.find(item => item.id === DEFAULT_LLM_MODEL)?.id || models[0]?.id || DEFAULT_LLM_MODEL;
        const selectedModel = models.find(item => item.id === resolvedModel);

        setForm({
            subscription_mode: data.subscription_mode || 'platform',
            provider,
            model: resolvedModel,
            temperature: typeof data.temperature === 'number' ? clampTemperature(data.temperature) : DEFAULT_TEMPERATURE,
            reasoning_effort: normalizeReasoningEffort(data.reasoning_effort, selectedModel),
        });
        setInitialSubscriptionMode((data.subscription_mode || 'platform') as 'platform' | 'byo_key');
        setProviderKeys(providerKeyStateFromItems(data.provider_keys));
        setProviderKeyTouched(emptyProviderKeyTouchedState());
        setProviderStatuses(data.provider_statuses || []);

        setCapabilities({
            can_configure_byo: typeof data.can_configure_byo === 'boolean' ? data.can_configure_byo : !isSuperAdmin,
            can_configure_platform: typeof data.can_configure_platform === 'boolean' ? data.can_configure_platform : isSuperAdmin,
            can_edit_model_defaults: data.can_edit_model_defaults ?? true,
            can_edit_route_overrides: data.can_edit_route_overrides ?? true,
            can_edit_provider_keys: data.can_edit_provider_keys ?? isSuperAdmin,
            has_stored_byo_keys: data.has_stored_byo_keys ?? false,
            platform_mode_locked: !!data.platform_mode_locked,
            platform_mode_lock_reason: data.platform_mode_lock_reason || null,
        });
    }
    catch (e: any)
    {
        setError(e?.message || 'Failed to load configuration');
    }
    finally
    {
        setLoading(false);
    }
}

/**
 * loadPrompts
 * Loads prompt configuration from API.
 */
async function loadPrompts(
    setPrompts: React.Dispatch<React.SetStateAction<PromptConfig[]>>,
    setActivePromptType: React.Dispatch<React.SetStateAction<string>>,
    currentActiveType: string
): Promise<void>
{
    try
    {
        const res = await apiClient.get(API_ENDPOINTS.PROMPTS);
        const loaded: PromptConfig[] = [];
        const raw = res.data.prompts || [];
        for (const item of raw)
        {
            loaded.push({
                agent_name: item.agent_name,
                content: item.content,
                description: item.description,
                type: item.type || PROMPT_TYPE_DEFAULT,
            });
        }
        setPrompts(loaded);
        if (loaded.length > 0)
        {
            const defaultType = loaded[0].type || PROMPT_TYPE_DEFAULT;
            let nextType = defaultType;
            for (const prompt of loaded)
            {
                if (prompt.type === currentActiveType)
                {
                    nextType = currentActiveType;
                    break;
                }
            }
            setActivePromptType(nextType);
        }
    }
    catch
    {
        /* keep silent on prompt load errors */
    }
}

/**
 * handleInitialLoad
 * Runs on mount to fetch configuration and prompts.
 */
function handleInitialLoad(
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
    setForm: React.Dispatch<React.SetStateAction<LLMConfig>>,
    setProviderKeys: React.Dispatch<React.SetStateAction<ProviderKeyFormState>>,
    setProviderKeyTouched: React.Dispatch<React.SetStateAction<ProviderKeyTouchedState>>,
    setProviderStatuses: React.Dispatch<React.SetStateAction<LLMProviderStatusItem[]>>,
    setInitialSubscriptionMode: React.Dispatch<React.SetStateAction<'platform' | 'byo_key'>>,
    setAvailableModels: React.Dispatch<React.SetStateAction<LLMModelInfo[]>>,
    setCapabilities: React.Dispatch<React.SetStateAction<LLMConfigCapabilities>>,
    isSuperAdmin: boolean,
    setPrompts: React.Dispatch<React.SetStateAction<PromptConfig[]>>,
    setActivePromptType: React.Dispatch<React.SetStateAction<string>>,
    currentActiveType: string
): void
{
    void loadConfig(setLoading, setError, setForm, setProviderKeys, setProviderKeyTouched, setProviderStatuses, setInitialSubscriptionMode, setAvailableModels, setCapabilities, isSuperAdmin);
    if (isSuperAdmin)
    {
        void loadPrompts(setPrompts, setActivePromptType, currentActiveType);
    }
}

/**
 * handlePromptSave
 * Saves a single prompt.
 */
async function handlePromptSave(
    prompts: PromptConfig[],
    setPromptSaving: React.Dispatch<React.SetStateAction<boolean>>,
    setPrompts: React.Dispatch<React.SetStateAction<PromptConfig[]>>,
    setActivePromptType: React.Dispatch<React.SetStateAction<string>>,
    setSuccess: React.Dispatch<React.SetStateAction<string | null>>,
    currentActiveType: string,
    agentName: string
): Promise<void>
{
    if (!agentName)
    {
        return;
    }
    let selected: PromptConfig | null = null;
    for (const prompt of prompts)
    {
        if (prompt.agent_name === agentName)
        {
            selected = prompt;
            break;
        }
    }
    if (!selected)
    {
        return;
    }
    setPromptSaving(true);
    try
    {
        const payload = {
            prompts: [
                {
                    agent_name: selected.agent_name,
                    content: selected.content,
                    description: selected.description,
                    type: selected.type,
                },
            ],
        };
        const res = await apiClient.put(API_ENDPOINTS.PROMPTS, payload);
        const saved: PromptConfig[] = [];
        const items = res.data.prompts || [];
        for (const item of items)
        {
            saved.push({
                agent_name: item.agent_name,
                content: item.content,
                description: item.description,
                type: item.type || PROMPT_TYPE_DEFAULT,
            });
        }
        setPrompts(saved);
        let nextType = saved.length > 0 ? (saved[0]?.type || PROMPT_TYPE_DEFAULT) : PROMPT_TYPE_DEFAULT;
        for (const item of saved)
        {
            if (item.type === currentActiveType)
            {
                nextType = currentActiveType;
                break;
            }
        }
        setActivePromptType(nextType);
        setSuccess('Prompt saved');
        scheduleClearSuccess(setSuccess);
    }
    catch
    {
        /* ignore prompt save errors */
    }
    finally
    {
        setPromptSaving(false);
    }
}

/**
 * buildModelOptions
 * Builds model <option> elements.
 */
/**
 * buildPromptCards
 * Renders cards for active prompt type.
 */
function buildPromptCards(
    prompts: PromptConfig[],
    activeType: string,
    promptSaving: boolean,
    onContentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
    onSave: (e: React.MouseEvent<HTMLButtonElement>) => void
): React.ReactElement[]
{
    const cards: React.ReactElement[] = [];
    for (const prompt of prompts)
    {
        if (prompt.type !== activeType)
        {
            continue;
        }
        cards.push(
            <div className="ac-prompt-card" key={prompt.agent_name}>
                <div className="ac-prompt-card-header">
                    <div className="ac-prompt-card-meta">
                        <h3 className="ac-prompt-agent-name">{prompt.agent_name}</h3>
                        {prompt.description && (
                            <p className="ac-prompt-description">{prompt.description}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="ac-save-btn"
                        data-agent-name={prompt.agent_name}
                        disabled={promptSaving}
                        onClick={onSave}
                    >
                        {promptSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
                <textarea
                    className="ac-prompt-textarea"
                    rows={14}
                    data-agent-name={prompt.agent_name}
                    value={prompt.content}
                    onChange={onContentChange}
                    aria-label={`${prompt.agent_name} prompt`}
                />
            </div>,
        );
    }
    return cards;
}

/* ============================================================
   Component
   ============================================================ */

/**
 * AdminConfigNew
 * Redesigned configuration page matching the Administration screenshots.
 */
function AdminConfigNew(): React.ReactElement
{
    const { user } = useAuth();
    const isSuperAdmin = useMemo(() => String(user?.role || '').toLowerCase() === 'superadmin', [user?.role]);

    const [activeTab, setActiveTab] = useState<'llm' | 'billing' | 'prompts' | 'org-prompts'>('llm');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [promptSaving, setPromptSaving] = useState(false);
    const [prompts, setPrompts] = useState<PromptConfig[]>([]);
    const [availableModels, setAvailableModels] = useState<LLMModelInfo[]>([]);
    const [routeModels, setRouteModels] = useState<LLMModelInfo[]>([]);
    const [routeForms, setRouteForms] = useState<LLMRouteFormItem[]>([]);
    const [routesDirty, setRoutesDirty] = useState(false);
    const [routesLoading, setRoutesLoading] = useState(false);
    const [activePromptType, setActivePromptType] = useState(PROMPT_TYPE_DEFAULT);
    const [vectorKeyVisible, setVectorKeyVisible] = useState(false);
    const [stripeSecretVisible, setStripeSecretVisible] = useState(false);
    const [stripeWebhookSecretVisible, setStripeWebhookSecretVisible] = useState(false);
    const [stripePublishableVisible, setStripePublishableVisible] = useState(false);
    const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);
    const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
    const [reasoningDropdownOpen, setReasoningDropdownOpen] = useState(false);
    const [routeDropdownOpenKey, setRouteDropdownOpenKey] = useState<string | null>(null);
    const [providerLoading, setProviderLoading] = useState(false);
    const [configCapabilities, setConfigCapabilities] = useState<LLMConfigCapabilities>({
        can_configure_byo: !isSuperAdmin,
        can_configure_platform: isSuperAdmin,
        can_edit_model_defaults: true,
        can_edit_route_overrides: true,
        can_edit_provider_keys: isSuperAdmin,
        has_stored_byo_keys: false,
        platform_mode_locked: false,
        platform_mode_lock_reason: null,
    });
    const [providerKeys, setProviderKeys] = useState<ProviderKeyFormState>(emptyProviderKeyState);
    const [providerKeyTouched, setProviderKeyTouched] = useState<ProviderKeyTouchedState>(emptyProviderKeyTouchedState);
    const [providerStatuses, setProviderStatuses] = useState<LLMProviderStatusItem[]>([]);
    const [providerKeyVisibility, setProviderKeyVisibility] = useState<Record<string, boolean>>({
        openai: false,
        gemini: false,
        anthropic: false,
    });

    // Org custom prompt instructions (org-admin tab)
    const [orgInstructions, setOrgInstructions] = useState<OrgPromptInstructionItem[]>([]);
    const [orgInstructionsLoading, setOrgInstructionsLoading] = useState(false);
    const [orgInstructionsSaving, setOrgInstructionsSaving] = useState<string | null>(null); // agent name being saved
    const [orgActiveType, setOrgActiveType] = useState(PROMPT_TYPE_DEFAULT);
    const [orgGuidelinesOpen, setOrgGuidelinesOpen] = useState(false);

    // Vector ingestion key (superadmin only)
    const [vectorIngestionKey, setVectorIngestionKey] = useState('');
    const [vectorKeyTouched, setVectorKeyTouched] = useState(false);
    const [vectorKeyConfigured, setVectorKeyConfigured] = useState(false);

    // Global billing settings (superadmin only)
    const [stripeSecretKey, setStripeSecretKey] = useState('');
    const [stripeSecretKeyTouched, setStripeSecretKeyTouched] = useState(false);
    const [stripeSecretKeyConfigured, setStripeSecretKeyConfigured] = useState(false);
    const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
    const [stripeWebhookSecretTouched, setStripeWebhookSecretTouched] = useState(false);
    const [stripeWebhookSecretConfigured, setStripeWebhookSecretConfigured] = useState(false);
    const [stripePublishableKey, setStripePublishableKey] = useState('');
    const [llmTokenProfitPercentage, setLlmTokenProfitPercentage] = useState('0');
    const [llmUsageRechargeAmountUsd, setLlmUsageRechargeAmountUsd] = useState('50');
    const [llmUsageAutoRechargeThresholdUsd, setLlmUsageAutoRechargeThresholdUsd] = useState('5');

    const [form, setForm] = useState<LLMConfig>({
        subscription_mode: 'platform',
        provider: 'openai',
        model: DEFAULT_LLM_MODEL,
        temperature: DEFAULT_TEMPERATURE,
        reasoning_effort: DEFAULT_REASONING_EFFORT,
    });
    const [initialSubscriptionMode, setInitialSubscriptionMode] = useState<'platform' | 'byo_key'>('platform');

    const providerDropdownRef = useRef<HTMLDivElement>(null);
    const modelDropdownRef = useRef<HTMLDivElement>(null);
    const reasoningDropdownRef = useRef<HTMLDivElement>(null);
    const routeDropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const tempSliderRef = useRef<HTMLInputElement>(null);

    /* ---- Derived values ---- */

    function buildPromptGroupsMemo(): Record<string, PromptConfig[]>
    {
        return buildPromptGroups(prompts);
    }

    function buildPromptTypeOrderMemo(): string[]
    {
        return buildPromptTypeOrder(promptGroups);
    }

    const promptGroups = useMemo(buildPromptGroupsMemo, [prompts]);
    const promptTypeOrder = useMemo(buildPromptTypeOrderMemo, [promptGroups]);
    const selectedModel = useMemo(
        () => availableModels.find(model => model.id === normalizeModelValue(form.model)) || null,
        [availableModels, form.model],
    );
    const canConfigureModelSettings = useMemo(
        () => configCapabilities.can_edit_model_defaults,
        [configCapabilities.can_edit_model_defaults],
    );
    const canConfigureRoutes = useMemo(
        () => configCapabilities.can_edit_route_overrides,
        [configCapabilities.can_edit_route_overrides],
    );
    const providerCredentialsEditable = useMemo(
        () => configCapabilities.can_edit_provider_keys,
        [configCapabilities.can_edit_provider_keys],
    );
    const showOrgLlmCustomization = useMemo(
        () => isSuperAdmin || form.subscription_mode === 'byo_key',
        [form.subscription_mode, isSuperAdmin],
    );
    const providerStatusesByProvider = useMemo(
        () => providerStatusMap(providerStatuses),
        [providerStatuses],
    );
    const effectiveReasoningEffort = useMemo(
        () => normalizeReasoningEffort(form.reasoning_effort, selectedModel),
        [form.reasoning_effort, selectedModel],
    );
    const supportsReasoning = useMemo(
        () => selectedModel?.supports_reasoning ?? true,
        [selectedModel],
    );
    const reasoningOptions = useMemo(() =>
    {
        if (selectedModel?.supports_reasoning === false)
        {
            return [];
        }
        const options = selectedModel?.reasoning_effort_options || [];
        return options.length > 0 ? options : ['none', 'low', 'medium', 'high', 'xhigh'];
    }, [selectedModel]);
    const temperatureEnabled = useMemo(
        () => canAdjustTemperature(selectedModel, effectiveReasoningEffort),
        [selectedModel, effectiveReasoningEffort],
    );
    const temperatureSupportMessage = useMemo(
        () => getTemperatureSupportMessage(selectedModel, effectiveReasoningEffort),
        [selectedModel, effectiveReasoningEffort],
    );
    const unavailableModelNotice = useMemo(
        () => getUnavailableModelNotice(form.model, availableModels),
        [availableModels, form.model],
    );

    async function loadVectorSettings(): Promise<void>
    {
        try
        {
            const res = await getVectorIngestionKeyStatus();
            setVectorKeyConfigured(res.is_configured);
            setVectorIngestionKey(res.api_key || '');
            setVectorKeyTouched(false);
            setVectorKeyVisible(false);
        }
        catch
        {
            // non-fatal
        }
    }

    async function loadBillingSettings(): Promise<void>
    {
        try
        {
            const res = await getGlobalBillingSettings();
            setStripeSecretKeyConfigured(res.stripe_secret_key_configured);
            setStripeSecretKey(res.stripe_secret_key || '');
            setStripeWebhookSecretConfigured(res.stripe_webhook_secret_configured);
            setStripeWebhookSecret(res.stripe_webhook_secret || '');
            setStripePublishableKey(res.stripe_publishable_key || '');
            setLlmTokenProfitPercentage(String(res.llm_token_profit_percentage ?? 0));
            setLlmUsageRechargeAmountUsd(String(res.llm_usage_recharge_amount_usd ?? 50));
            setLlmUsageAutoRechargeThresholdUsd(String(res.llm_usage_auto_recharge_threshold_usd ?? 5));
            setStripeSecretKeyTouched(false);
            setStripeWebhookSecretTouched(false);
            setStripeSecretVisible(false);
            setStripeWebhookSecretVisible(false);
            setStripePublishableVisible(false);
        }
        catch
        {
            // non-fatal
        }
    }

    async function loadRouteSettings(): Promise<void>
    {
        setRoutesLoading(true);
        try
        {
            const [routesData, models] = await Promise.all([
                getLLMRoutes(),
                getLLMModels().catch(() => []),
            ]);
            setRouteModels(models);
            setRouteForms((routesData.routes || [])
                .filter(item => !HIDDEN_MODEL_ROUTE_KEYS.has(item.route_key))
                .map(item => routeItemToForm(item, models)));
            setRoutesDirty(false);
        }
        catch
        {
            setRouteForms([]);
            setRoutesDirty(false);
        }
        finally
        {
            setRoutesLoading(false);
        }
    }

    /* ---- Effects ---- */

    useEffect(function initEffect() {
        handleInitialLoad(
            setLoading,
            setError,
            setForm,
            setProviderKeys,
            setProviderKeyTouched,
            setProviderStatuses,
            setInitialSubscriptionMode,
            setAvailableModels,
            setConfigCapabilities,
            isSuperAdmin,
            setPrompts,
            setActivePromptType,
            PROMPT_TYPE_DEFAULT,
        );
        void loadRouteSettings();
        // Load saved vector ingestion key for superadmin
        if (isSuperAdmin) {
            void loadVectorSettings();
            void loadBillingSettings();
        }
        // Load org prompt instructions for org admins (superadmins skip this tab)
        if (!isSuperAdmin) {
            setOrgInstructionsLoading(true);
            getOrgPromptInstructions()
                .then(data => {
                    setOrgInstructions(data.instructions);
                    if (data.instructions.length > 0) {
                        setOrgActiveType(data.instructions[0].prompt_type || PROMPT_TYPE_DEFAULT);
                    }
                })
                .catch(() => { /* silent */ })
                .finally(() => setOrgInstructionsLoading(false));
        }
    }, [isSuperAdmin]);

    useEffect(function reasoningSyncEffect()
    {
        const normalized = normalizeReasoningEffort(form.reasoning_effort, selectedModel);
        if (normalized !== form.reasoning_effort)
        {
            setForm(prev => ({
                ...prev,
                reasoning_effort: normalized,
            }));
        }
    }, [form.reasoning_effort, selectedModel]);

    useEffect(function tempSliderFillEffect()
    {
        if (tempSliderRef.current && temperatureEnabled)
        {
            const pct = (form.temperature / 2) * 100;
            tempSliderRef.current.style.background =
                `linear-gradient(to right, #0d6efd ${pct}%, #dee2e6 ${pct}%)`;
        }
    }, [form.temperature, temperatureEnabled]);

    useEffect(function clickOutsideEffect()
    {
        function handleDocClick(e: MouseEvent): void
        {
            if (providerDropdownRef.current && !providerDropdownRef.current.contains(e.target as Node))
            {
                setProviderDropdownOpen(false);
            }
            if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node))
            {
                setModelDropdownOpen(false);
            }
            if (reasoningDropdownRef.current && !reasoningDropdownRef.current.contains(e.target as Node))
            {
                setReasoningDropdownOpen(false);
            }
            if (routeDropdownOpenKey)
            {
                const routeDropdownRef = routeDropdownRefs.current[routeDropdownOpenKey];
                if (routeDropdownRef && !routeDropdownRef.contains(e.target as Node))
                {
                    setRouteDropdownOpenKey(null);
                }
            }
        }
        document.addEventListener('mousedown', handleDocClick);
        return () => document.removeEventListener('mousedown', handleDocClick);
    }, [routeDropdownOpenKey]);

    /* ---- Handlers ---- */

    function handleTabClick(e: React.MouseEvent<HTMLButtonElement>): void
    {
        const rawKey = e.currentTarget.dataset.tab;
        const key: 'llm' | 'billing' | 'prompts' | 'org-prompts' =
            rawKey === 'billing' || rawKey === 'prompts' || rawKey === 'org-prompts'
                ? rawKey
                : 'llm';
        setActiveTab(key);
    }

    function handleLlmResetClick(): void
    {
        setError(null);
        setSuccess(null);
        void loadConfig(
            setLoading,
            setError,
            setForm,
            setProviderKeys,
            setProviderKeyTouched,
            setProviderStatuses,
            setInitialSubscriptionMode,
            setAvailableModels,
            setConfigCapabilities,
            isSuperAdmin,
        );
        void loadRouteSettings();
        if (isSuperAdmin)
        {
            void loadVectorSettings();
        }
    }

    function handleBillingResetClick(): void
    {
        setError(null);
        setSuccess(null);
        void loadBillingSettings();
    }

    function handleProfitPercentageChange(e: React.ChangeEvent<HTMLInputElement>): void
    {
        const value = e.currentTarget.value;
        if (value === '')
        {
            setLlmTokenProfitPercentage('');
            return;
        }

        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 100)
        {
            setLlmTokenProfitPercentage(value);
        }
    }

    function handleUsdInputChange(
        e: React.ChangeEvent<HTMLInputElement>,
        setter: React.Dispatch<React.SetStateAction<string>>,
    ): void
    {
        const value = e.currentTarget.value;
        if (value === '')
        {
            setter('');
            return;
        }
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed >= 0)
        {
            setter(value);
        }
    }

    function usdToCents(value: string): number
    {
        return Math.round(Number(value || '0') * 100);
    }

    function handleProviderKeyVisibilityToggle(provider: keyof ProviderKeyFormState): void
    {
        setProviderKeyVisibility(prev => ({
            ...prev,
            [provider]: !prev[provider],
        }));
    }

    function handleProviderKeyChange(provider: keyof ProviderKeyFormState, value: string): void
    {
        setProviderKeys(prev => ({
            ...prev,
            [provider]: value,
        }));
        setProviderKeyTouched(prev => ({
            ...prev,
            [provider]: true,
        }));
    }

    function isProviderAvailableInCurrentMode(provider: string): boolean
    {
        const statusItem = providerStatusesByProvider[normalizeProviderValue(provider)];
        if (!statusItem)
        {
            return true;
        }
        return form.subscription_mode === 'byo_key'
            ? statusItem.byo_configured
            : statusItem.platform_configured;
    }

    function getProviderLabel(provider: string): string
    {
        return PROVIDER_OPTIONS.find(option => option.value === normalizeProviderValue(provider))?.label || provider;
    }

    function getProviderUnavailableReason(provider: string): string
    {
        const statusItem = providerStatusesByProvider[normalizeProviderValue(provider)];
        if (!statusItem)
        {
            return '';
        }
        if (form.subscription_mode === 'byo_key')
        {
            return statusItem.byo_configured ? '' : 'No BYO key is configured for this provider.';
        }
        return statusItem.platform_configured ? '' : 'No platform key is configured for this provider.';
    }

    async function syncModelsForProvider(nextProvider: string, preferredModel?: string, preferredReasoningEffort?: string): Promise<void>
    {
        const normalizedProvider = normalizeProviderValue(nextProvider);
        setProviderLoading(true);
        setProviderDropdownOpen(false);
        setModelDropdownOpen(false);
        setReasoningDropdownOpen(false);
        setRouteDropdownOpenKey(null);
        try
        {
            const models = await getLLMModels(normalizedProvider).catch(() => []);
            const normalizedPreferredModel = normalizeModelValue(preferredModel);
            const nextSelectedModel = models.find(model => model.id === normalizedPreferredModel) || models[0] || null;

            setAvailableModels(models);
            setForm(prev => ({
                ...prev,
                provider: normalizedProvider,
                model: nextSelectedModel?.id || normalizedPreferredModel,
                reasoning_effort: normalizeReasoningEffort(preferredReasoningEffort ?? prev.reasoning_effort, nextSelectedModel),
            }));
        }
        finally
        {
            setProviderLoading(false);
        }
    }

    function handleModelDropdownToggle(): void
    {
        setProviderDropdownOpen(false);
        setReasoningDropdownOpen(false);
        setRouteDropdownOpenKey(null);
        setModelDropdownOpen(o => !o);
    }

    function handleModelSelect(e: React.MouseEvent<HTMLButtonElement>): void
    {
        const value = e.currentTarget.dataset.value || '';
        const nextModel = availableModels.find(model => model.id === value) || null;
        setForm(prev => ({
            ...prev,
            model: value,
            reasoning_effort: normalizeReasoningEffort(prev.reasoning_effort, nextModel),
        }));
        setProviderDropdownOpen(false);
        setModelDropdownOpen(false);
        setReasoningDropdownOpen(false);
        setRouteDropdownOpenKey(null);
    }

    function handleReasoningDropdownToggle(): void
    {
        setProviderDropdownOpen(false);
        setModelDropdownOpen(false);
        setRouteDropdownOpenKey(null);
        setReasoningDropdownOpen(o => !o);
    }

    function handleReasoningSelect(e: React.MouseEvent<HTMLButtonElement>): void
    {
        const value = e.currentTarget.dataset.value || '';
        setForm(prev => ({
            ...prev,
            reasoning_effort: value,
        }));
        setReasoningDropdownOpen(false);
        setRouteDropdownOpenKey(null);
    }

    function handleProviderDropdownToggle(): void
    {
        setModelDropdownOpen(false);
        setReasoningDropdownOpen(false);
        setRouteDropdownOpenKey(null);
        setProviderDropdownOpen(o => !o);
    }

    function handleProviderSelect(provider: string): void
    {
        setProviderDropdownOpen(false);
        void syncModelsForProvider(provider, form.model, form.reasoning_effort);
    }

    function handleRouteDropdownToggle(dropdownKey: string): void
    {
        setProviderDropdownOpen(false);
        setModelDropdownOpen(false);
        setReasoningDropdownOpen(false);
        setRouteDropdownOpenKey(current => current === dropdownKey ? null : dropdownKey);
    }

    function handleRouteProviderSelect(routeKey: string, provider: string): void
    {
        const normalizedProvider = normalizeProviderValue(provider);
        setRoutesDirty(true);
        setRouteForms(prev => prev.map(route => {
            if (route.route_key !== routeKey)
            {
                return route;
            }
            const models = getRouteModelOptions(normalizedProvider);
            const nextModel = models.find(item => item.id === route.model) || models[0] || null;
            return {
                ...route,
                provider: normalizedProvider,
                model: nextModel?.id || route.model,
                reasoning_effort: normalizeReasoningEffort(route.reasoning_effort, nextModel),
            };
        }));
        setRouteDropdownOpenKey(null);
    }

    function handleRouteModelDropdownSelect(routeKey: string, model: string): void
    {
        const selectedModel = routeModels.find(item => item.id === model) || availableModels.find(item => item.id === model) || null;
        setRoutesDirty(true);
        setRouteForms(prev => prev.map(route => (
            route.route_key === routeKey
                ? { ...route, model, reasoning_effort: normalizeReasoningEffort(route.reasoning_effort, selectedModel) }
                : route
        )));
        setRouteDropdownOpenKey(null);
    }

    function handleRouteReasoningDropdownSelect(routeKey: string, reasoningEffort: string): void
    {
        setRoutesDirty(true);
        setRouteForms(prev => prev.map(route => (
            route.route_key === routeKey ? { ...route, reasoning_effort: reasoningEffort } : route
        )));
        setRouteDropdownOpenKey(null);
    }

    function isRouteDropdownOpen(dropdownKey: string): boolean
    {
        return routeDropdownOpenKey === dropdownKey;
    }

    function handleFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;
        if (name === 'temperature') {
            const parsed = Number(value);
            setForm(prev => ({ ...prev, temperature: clampTemperature(parsed) }));
            return;
        }
        if (name === 'reasoning_effort') {
            setForm(prev => ({ ...prev, reasoning_effort: value }));
            return;
        }
        if (name === 'provider') {
            void syncModelsForProvider(value, form.model, form.reasoning_effort);
            return;
        }
        if (name === 'subscription_mode') {
            setForm(prev => ({
                ...prev,
                subscription_mode: value as 'platform' | 'byo_key',
            }));
            return;
        }
        setForm(prev => ({ ...prev, [name]: value }));
    }

    function getRouteModelOptions(provider: string): LLMModelInfo[]
    {
        const normalizedProvider = normalizeProviderValue(provider);
        const models = routeModels.filter(item => normalizeProviderValue(item.id.split('/', 1)[0]) === normalizedProvider);
        return models.length > 0 ? models : availableModels.filter(item => normalizeProviderValue(item.id.split('/', 1)[0]) === normalizedProvider);
    }

    function getRouteUnavailableNotice(route: LLMRouteFormItem, modelOptions: LLMModelInfo[]): string
    {
        if (!isProviderAvailableInCurrentMode(route.provider))
        {
            return getProviderUnavailableReason(route.provider);
        }
        return getUnavailableModelNotice(route.model, modelOptions);
    }

    function handleRouteEnabledChange(e: React.ChangeEvent<HTMLInputElement>): void
    {
        const routeKey = e.currentTarget.dataset.routeKey || '';
        const checked = e.currentTarget.checked;
        setRoutesDirty(true);
        setRouteForms(prev => prev.map(route => {
            if (route.route_key !== routeKey)
            {
                return route;
            }
            if (checked)
            {
                return { ...route, enabled: true };
            }
            return {
                ...route,
                enabled: false,
                provider: normalizeProviderValue(form.provider),
                model: normalizeModelValue(form.model),
                temperature: clampTemperature(form.temperature),
                reasoning_effort: effectiveReasoningEffort,
            };
        }));
    }

    function handleRouteProviderChange(e: React.ChangeEvent<HTMLSelectElement>): void
    {
        const routeKey = e.currentTarget.dataset.routeKey || '';
        const provider = normalizeProviderValue(e.currentTarget.value);
        const models = getRouteModelOptions(provider);
        setRoutesDirty(true);
        setRouteForms(prev => prev.map(route => {
            if (route.route_key !== routeKey)
            {
                return route;
            }
            const nextModel = models.find(item => item.id === route.model) || models[0] || null;
            return {
                ...route,
                provider,
                model: nextModel?.id || route.model,
                reasoning_effort: normalizeReasoningEffort(route.reasoning_effort, nextModel),
            };
        }));
    }

    function handleRouteModelChange(e: React.ChangeEvent<HTMLSelectElement>): void
    {
        const routeKey = e.currentTarget.dataset.routeKey || '';
        const model = normalizeModelValue(e.currentTarget.value);
        const selectedModel = routeModels.find(item => item.id === model) || availableModels.find(item => item.id === model) || null;
        setRoutesDirty(true);
        setRouteForms(prev => prev.map(route => (
            route.route_key === routeKey
                ? { ...route, model, reasoning_effort: normalizeReasoningEffort(route.reasoning_effort, selectedModel) }
                : route
        )));
    }

    function handleRouteTemperatureChange(e: React.ChangeEvent<HTMLInputElement>): void
    {
        const routeKey = e.currentTarget.dataset.routeKey || '';
        const temperature = clampTemperature(Number(e.currentTarget.value));
        setRoutesDirty(true);
        setRouteForms(prev => prev.map(route => (
            route.route_key === routeKey ? { ...route, temperature } : route
        )));
    }

    function handleRouteReasoningChange(e: React.ChangeEvent<HTMLSelectElement>): void
    {
        const routeKey = e.currentTarget.dataset.routeKey || '';
        const reasoning_effort = e.currentTarget.value;
        setRoutesDirty(true);
        setRouteForms(prev => prev.map(route => (
            route.route_key === routeKey ? { ...route, reasoning_effort } : route
        )));
    }

    async function handleBillingSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();
        const nextProfitPercentage = Number(llmTokenProfitPercentage || '0');
        if (!Number.isFinite(nextProfitPercentage) || nextProfitPercentage < 0 || nextProfitPercentage > 100) {
            setError('LLM token profit percentage must be between 0 and 100.');
            return;
        }
        const rechargeAmountCents = usdToCents(llmUsageRechargeAmountUsd);
        const thresholdCents = usdToCents(llmUsageAutoRechargeThresholdUsd);
        if (!Number.isFinite(rechargeAmountCents) || rechargeAmountCents <= 0) {
            setError('LLM Usage Credits recharge amount must be greater than $0.00.');
            return;
        }
        if (!Number.isFinite(thresholdCents) || thresholdCents < 0 || thresholdCents >= rechargeAmountCents) {
            setError('Auto-recharge threshold must be greater than or equal to $0.00 and less than the recharge amount.');
            return;
        }
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const billingPayload: Parameters<typeof updateGlobalBillingSettings>[0] = {
                stripe_publishable_key: stripePublishableKey.trim(),
                llm_token_profit_percentage: nextProfitPercentage,
                llm_usage_recharge_amount_cents: rechargeAmountCents,
                llm_usage_auto_recharge_threshold_cents: thresholdCents,
                llm_usage_currency: 'usd',
            };
            if (stripeSecretKeyTouched) {
                billingPayload.stripe_secret_key = stripeSecretKey.trim();
            }
            if (stripeWebhookSecretTouched) {
                billingPayload.stripe_webhook_secret = stripeWebhookSecret.trim();
            }
            const billingSaved = await updateGlobalBillingSettings(billingPayload);
            setStripeSecretKeyConfigured(billingSaved.stripe_secret_key_configured);
            setStripeSecretKey(billingSaved.stripe_secret_key || '');
            setStripeWebhookSecretConfigured(billingSaved.stripe_webhook_secret_configured);
            setStripeWebhookSecret(billingSaved.stripe_webhook_secret || '');
            setStripePublishableKey(billingSaved.stripe_publishable_key || '');
            setLlmTokenProfitPercentage(String(billingSaved.llm_token_profit_percentage ?? 0));
            setLlmUsageRechargeAmountUsd(String(billingSaved.llm_usage_recharge_amount_usd ?? 50));
            setLlmUsageAutoRechargeThresholdUsd(String(billingSaved.llm_usage_auto_recharge_threshold_usd ?? 5));
            setStripeSecretKeyTouched(false);
            setStripeWebhookSecretTouched(false);
            setStripeSecretVisible(false);
            setStripeWebhookSecretVisible(false);
            setStripePublishableVisible(false);
            setSuccess('Stripe & LLM Usage Credits settings saved successfully');
            scheduleClearSuccess(setSuccess);
        } catch (err: any) {
            setError(err?.message || 'Failed to save Stripe & LLM Usage Credits settings.');
        } finally {
            setSaving(false);
        }
    }

    async function handleConfigSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();
        if (isSuperAdmin && !vectorKeyConfigured && !vectorIngestionKey.trim()) {
            setError('Vector Ingestion API Key is required.');
            return;
        }
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            if (isSuperAdmin && vectorKeyTouched) {
                const saved = await updateVectorIngestionKey(vectorIngestionKey.trim());
                setVectorKeyConfigured(saved.is_configured);
                setVectorIngestionKey(saved.api_key || '');
                setVectorKeyTouched(false);
                setVectorKeyVisible(false);
            }

            const payload: Parameters<typeof updateLLMConfig>[0] = {};

            if (configCapabilities.can_configure_byo) {
                payload.subscription_mode = form.subscription_mode;
                if (
                    initialSubscriptionMode === 'byo_key'
                    && form.subscription_mode === 'platform'
                    && configCapabilities.has_stored_byo_keys
                    && !isSuperAdmin
                ) {
                    payload.byo_key_retention = window.confirm(
                        'Switching to Platform mode will stop using your organization BYO keys. Click OK to delete the stored BYO keys, or Cancel to keep them saved for later use.',
                    ) ? 'delete' : 'keep';
                }
            }

            if (providerCredentialsEditable) {
                const touchedProviderKeys = Object.entries(providerKeyTouched).reduce((acc, [provider, touched]) => {
                    if (touched) {
                        acc[provider] = providerKeys[provider as keyof ProviderKeyFormState];
                    }
                    return acc;
                }, {} as Record<string, string>);
                if (Object.keys(touchedProviderKeys).length > 0) {
                    payload.provider_keys = touchedProviderKeys;
                }
            }

            if (canConfigureModelSettings) {
                payload.provider = form.provider;
                payload.model = normalizeModelValue(form.model);
                if (temperatureEnabled) {
                    payload.temperature = form.temperature;
                }
                payload.reasoning_effort = supportsReasoning ? effectiveReasoningEffort : null;
            }

            const updated = await updateLLMConfig(payload);
            let routesSynced = false;
            if (canConfigureRoutes && routesDirty && routeForms.length > 0) {
                const routePayload = routeForms.map(route => {
                    const routeSelectedModel = routeModels.find(item => item.id === normalizeModelValue(route.model))
                        || availableModels.find(item => item.id === normalizeModelValue(route.model))
                        || null;
                    return {
                        route_key: route.route_key,
                        enabled: route.enabled,
                        provider: route.provider,
                        model: normalizeModelValue(route.model),
                        temperature: canAdjustTemperature(routeSelectedModel, route.reasoning_effort) ? route.temperature : undefined,
                        reasoning_effort: routeSelectedModel?.supports_reasoning === false ? null : (route.reasoning_effort || null),
                    };
                });
                const routeResult = await updateLLMRoutes(routePayload);
                const allModels = routeModels.length > 0 ? routeModels : await getLLMModels().catch(() => []);
                setRouteModels(allModels);
                setRouteForms((routeResult.routes || []).map(item => routeItemToForm(item, allModels)));
                setRoutesDirty(false);
                routesSynced = true;
            }
            const updatedModelValue = normalizeModelValue(updated.model || form.model);
            const updatedModels = await getLLMModels(updated.provider || form.provider).catch(() => availableModels);
            const updatedModel = updatedModels.find(model => model.id === updatedModelValue) || null;
            setAvailableModels(updatedModels);
            setProviderKeys(providerKeyStateFromItems(updated.provider_keys));
            setProviderKeyTouched(emptyProviderKeyTouchedState());
            setProviderStatuses(updated.provider_statuses || []);
            setForm(prev => ({
                ...prev,
                provider: updated.provider || prev.provider,
                model: updatedModelValue,
                temperature: typeof updated.temperature === 'number' ? clampTemperature(updated.temperature) : prev.temperature,
                reasoning_effort: normalizeReasoningEffort(updated.reasoning_effort, updatedModel),
                subscription_mode: (updated.subscription_mode || prev.subscription_mode) as 'platform' | 'byo_key',
            }));
            setInitialSubscriptionMode((updated.subscription_mode || form.subscription_mode) as 'platform' | 'byo_key');
            setConfigCapabilities(prev => ({
                ...prev,
                can_configure_byo: updated.can_configure_byo ?? prev.can_configure_byo,
                can_configure_platform: updated.can_configure_platform ?? prev.can_configure_platform,
                can_edit_model_defaults: updated.can_edit_model_defaults ?? prev.can_edit_model_defaults,
                can_edit_route_overrides: updated.can_edit_route_overrides ?? prev.can_edit_route_overrides,
                can_edit_provider_keys: updated.can_edit_provider_keys ?? prev.can_edit_provider_keys,
                has_stored_byo_keys: updated.has_stored_byo_keys ?? prev.has_stored_byo_keys,
                platform_mode_locked: updated.platform_mode_locked ?? prev.platform_mode_locked,
                platform_mode_lock_reason: updated.platform_mode_lock_reason ?? prev.platform_mode_lock_reason,
            }));
            if (canConfigureRoutes && !routesSynced) {
                await loadRouteSettings();
            }
            setSuccess('Configuration saved successfully');
            scheduleClearSuccess(setSuccess);
        } catch (err: any) {
            setError(err?.message || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    }

    function handlePromptTypeClick(e: React.MouseEvent<HTMLButtonElement>): void
    {
        const key = e.currentTarget.dataset.type || PROMPT_TYPE_DEFAULT;
        setActivePromptType(key);
    }

    function handlePromptContentChangeEvent(e: React.ChangeEvent<HTMLTextAreaElement>): void
    {
        const agentName = e.currentTarget.dataset.agentName || '';
        const value = e.currentTarget.value;
        const next: PromptConfig[] = [];
        for (const prompt of prompts)
        {
            next.push(prompt.agent_name === agentName ? { ...prompt, content: value } : prompt);
        }
        setPrompts(next);
    }

    async function handlePromptSaveClick(e: React.MouseEvent<HTMLButtonElement>): Promise<void>
    {
        const agentName = e.currentTarget.dataset.agentName || '';
        await handlePromptSave(
            prompts,
            setPromptSaving,
            setPrompts,
            setActivePromptType,
            setSuccess,
            activePromptType,
            agentName,
        );
    }

    /* ---- Org prompt instruction handlers ---- */

    function handleOrgTypeClick(e: React.MouseEvent<HTMLButtonElement>): void {
        setOrgActiveType(e.currentTarget.dataset.type || PROMPT_TYPE_DEFAULT);
    }

    const ORG_INSTRUCTION_MAX_CHARS = 300;

    function handleOrgInstructionChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        const agentName = e.currentTarget.dataset.agentName || '';
        const value = e.currentTarget.value.slice(0, ORG_INSTRUCTION_MAX_CHARS);
        setOrgInstructions(prev =>
            prev.map(item =>
                item.agent_name === agentName
                    ? { ...item, custom_instructions: value || null }
                    : item,
            ),
        );
    }

    async function handleOrgInstructionSave(e: React.MouseEvent<HTMLButtonElement>): Promise<void> {
        const agentName = e.currentTarget.dataset.agentName || '';
        setOrgInstructionsSaving(agentName);
        setError(null);
        try {
            const updated = await updateOrgPromptInstructions(orgInstructions);
            setOrgInstructions(updated.instructions);
            setSuccess(`Instructions saved for ${agentName}`);
            scheduleClearSuccess(setSuccess);
        } catch {
            setError('Failed to save custom instructions.');
        } finally {
            setOrgInstructionsSaving(null);
        }
    }

    /* ---- Render ---- */

    if (loading) {
        return (
            <div className="ac-page">
                <div className="ac-page-loader">
                    <Spinner animation="border" role="status" className="ac-page-loader-spinner" />
                    <span className="ac-page-loader-text">Loading configuration…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="ac-page">
            {/* Page header */}
            <div className="ac-page-header">
                <h1 className="ac-page-title">Administration</h1>
                <p className="ac-page-subtitle">Manage LLM settings and AI agent prompts</p>
            </div>

            {/* Alerts */}
            {error && (
                <Alert variant="danger" className="ac-alert" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" className="ac-alert">
                    {success}
                </Alert>
            )}

            {/* Config box */}
            <div className="ac-box">
                {/* Tab row */}
                <div className="ac-tab-row">
                    <button
                        type="button"
                        data-tab="llm"
                        onClick={handleTabClick}
                        className={`ac-tab${activeTab === 'llm' ? ' active' : ''}`}
                    >
                        LLM Configuration
                    </button>
                    {isSuperAdmin && (
                        <button
                            type="button"
                            data-tab="prompts"
                            onClick={handleTabClick}
                            className={`ac-tab${activeTab === 'prompts' ? ' active' : ''}`}
                        >
                            Prompt Configuration
                        </button>
                    )}
                    {isSuperAdmin && (
                        <button
                            type="button"
                            data-tab="billing"
                            onClick={handleTabClick}
                            className={`ac-tab${activeTab === 'billing' ? ' active' : ''}`}
                        >
                            Stripe &amp; LLM Usage Credits
                        </button>
                    )}
                    {!isSuperAdmin && (
                        <button
                            type="button"
                            data-tab="org-prompts"
                            onClick={handleTabClick}
                            className={`ac-tab${activeTab === 'org-prompts' ? ' active' : ''}`}
                        >
                            Additional Prompt Instructions
                        </button>
                    )}
                </div>

                {/* ── LLM Configuration ── */}
                {activeTab === 'llm' && (
                    <>
                    <form className="ac-llm-content" onSubmit={handleConfigSubmit}>
                        {/* Subscription Mode Toggle */}
                        <section className="ac-section">
                            <h2 className="ac-section-title">Subscription Mode</h2>
                            {!isSuperAdmin ? (
                                <div
                                    className="ac-subscription-toggle-group"
                                    title={!configCapabilities.can_configure_byo ? 'Please ask your superadmin to enable BYO to configure this.' : undefined}
                                >
                                    <label>
                                        <input
                                            type="radio"
                                            name="subscription_mode"
                                            value="platform"
                                            checked={form.subscription_mode === 'platform'}
                                            onChange={handleFormChange}
                                            disabled={loading}
                                        />
                                        Use AcceliQA Platform Subscription
                                    </label>
                                    <label
                                        title={!configCapabilities.can_configure_byo ? 'Please ask your superadmin to enable BYO to configure this.' : undefined}
                                    >
                                        <input
                                            type="radio"
                                            name="subscription_mode"
                                            value="byo_key"
                                            checked={form.subscription_mode === 'byo_key'}
                                            onChange={handleFormChange}
                                            disabled={loading || !configCapabilities.can_configure_byo}
                                        />
                                        Use My Own API Key / Subscription
                                    </label>
                                    {!configCapabilities.can_configure_byo && (
                                        <p className="ac-control-note ac-byo-disabled-note">
                                            BYO key configuration is not enabled for your organization. Please ask your superadmin to enable this feature.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p className="ac-control-note">
                                    BYO key mode is organization-scoped and can only be configured by organization admins.
                                </p>
                            )}
                            <p className="ac-info-note">
                                <InfoCircleFill className="ac-info-icon" />
                                Choose whether to use AcceliQA's built-in LLM subscription or your own API key/provider.
                            </p>
                        </section>

                        {/* Vector Ingestion API Key (superadmin only) */}
                        {isSuperAdmin && (
                            <section className="ac-section">
                                <h2 className="ac-section-title">Vector Ingestion API Key</h2>
                                <div className="ac-provider-fields">
                                    <label className="ac-select-label" htmlFor="vector_ingestion_key">
                                        OpenAI API Key{!vectorKeyConfigured && <span aria-hidden="true"> *</span>}
                                    </label>
                                    <div className="ac-api-key-group">
                                        <input
                                            id="vector_ingestion_key"
                                            type={vectorKeyVisible ? 'text' : 'password'}
                                            className="ac-api-key-input"
                                            value={vectorIngestionKey}
                                            onChange={(e) => {
                                                setVectorIngestionKey(e.target.value);
                                                setVectorKeyTouched(true);
                                            }}
                                            disabled={saving}
                                            placeholder="Enter OpenAI API key for vector ingestion"
                                            autoComplete="off"
                                            required={!vectorKeyConfigured}
                                        />
                                        <button
                                            type="button"
                                            className="ac-hide-btn"
                                            onClick={() => setVectorKeyVisible(v => !v)}
                                            disabled={saving}
                                        >
                                            {vectorKeyVisible ? 'Hide' : 'Show'}
                                        </button>
                                    </div>
                                </div>
                                <p className="ac-info-note">
                                    <InfoCircleFill className="ac-info-icon" />
                                    Dedicated OpenAI key for embedding and vector ingestion across all organizations.
                                    Saved keys are shown as masked previews and take priority over the platform LLM key for vector operations.
                                </p>
                            </section>
                        )}

                        {showOrgLlmCustomization ? (
                        <>
                        {/* Provider/API fields */}
                        <section
                            className={`ac-section${!providerCredentialsEditable ? ' ac-section-byo-locked' : ''}`}
                            title={!providerCredentialsEditable ? 'Please ask your superadmin to enable BYO to configure this.' : undefined}
                        >
                            <h2 className="ac-section-title">{isSuperAdmin ? 'Platform Provider Keys' : 'Organization BYO Provider Keys'}</h2>
                            {PROVIDER_OPTIONS.map(option => {
                                const provider = normalizeProviderValue(option.value);
                                return (
                                    <div className="ac-provider-fields" key={provider}>
                                        <label className="ac-select-label" htmlFor={`provider_key_${provider}`}>
                                            {option.label}
                                        </label>
                                        <div className="ac-api-key-group">
                                            <input
                                                id={`provider_key_${provider}`}
                                                type={providerKeyVisibility[provider] ? 'text' : 'password'}
                                                value={providerKeys[provider]}
                                                onChange={(e) => handleProviderKeyChange(provider, e.target.value)}
                                                disabled={loading || !providerCredentialsEditable}
                                                placeholder="Enter provider API key"
                                                className="ac-api-key-input"
                                                autoComplete="off"
                                            />
                                            <button
                                                type="button"
                                                className="ac-hide-btn"
                                                onClick={() => handleProviderKeyVisibilityToggle(provider)}
                                                disabled={!providerCredentialsEditable}
                                            >
                                                {providerKeyVisibility[provider] ? 'Hide' : 'Show'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            <p className="ac-info-note">
                                <InfoCircleFill className="ac-info-icon" />
                                Keys are stored per provider. Runtime uses platform keys in Platform mode and organization BYO keys in BYO mode.
                            </p>
                        </section>

                        {/* Model Selection */}
                        <section className="ac-section">
                            <h2 className="ac-section-title">Model Selection</h2>
                            {providerLoading ? (
                                <div className="ac-dependent-loader" aria-live="polite">
                                    <Spinner animation="border" size="sm" />
                                    <span>Loading models and settings...</span>
                                </div>
                            ) : (
                            <>
                                <div className="ac-model-selection-grid">
                                    <div className="ac-model-provider-card">
                                        <span className="ac-select-label">Provider</span>
                                        <div className="ac-reasoning-dropdown-wrap ac-model-provider-wrap" ref={providerDropdownRef}>
                                            <button
                                                id="provider"
                                                type="button"
                                                className="ac-reasoning-trigger ac-model-provider-trigger"
                                                onClick={handleProviderDropdownToggle}
                                                disabled={loading || !canConfigureModelSettings}
                                                aria-haspopup="listbox"
                                                aria-expanded={providerDropdownOpen}
                                            >
                                                <span className="ac-reasoning-value">{getProviderLabel(form.provider)}</span>
                                                <ChevronDown className="ac-model-chevron" />
                                            </button>
                                            {providerDropdownOpen && (
                                                <div className="ac-model-dropdown ac-reasoning-options">
                                                    {PROVIDER_OPTIONS.map(option => {
                                                        const available = isProviderAvailableInCurrentMode(option.value);
                                                        const reason = getProviderUnavailableReason(option.value);
                                                        return (
                                                            <button
                                                                key={option.value}
                                                                type="button"
                                                                className={`ac-model-option${form.provider === option.value ? ' selected' : ''}`}
                                                                onClick={() => handleProviderSelect(option.value)}
                                                                disabled={!canConfigureModelSettings || !available}
                                                            >
                                                                <div className="ac-model-option-title">{option.label}</div>
                                                                {!available && (
                                                                    <div className="ac-model-option-meta">{reason || 'Unavailable'}</div>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="ac-model-card" ref={modelDropdownRef}>
                                        <span className="ac-select-label">Model</span>
                                        <button
                                        type="button"
                                        className="ac-model-btn"
                                        onClick={handleModelDropdownToggle}
                                        disabled={loading || !canConfigureModelSettings}
                                            >
                                                <div className="ac-model-info">
                                                    <div className="ac-model-top">
                                                        <span className="ac-model-name">{getModelDisplayName(form.model, availableModels)}</span>
                                                        {selectedModel && formatModelLimits(selectedModel) && (
                                                            <span className="ac-model-limits">{formatModelLimits(selectedModel)}</span>
                                                        )}
                                                        <span className="ac-model-badge">
                                                            {selectedModel?.supports_reasoning ? 'REASONING' : 'STANDARD'}
                                                        </span>
                                                    </div>
                                                    {!selectedModel && (
                                                        <span className="ac-model-provider">{form.model}</span>
                                                    )}
                                                </div>
                                                <ChevronDown className="ac-model-chevron" />
                                            </button>
                                    {modelDropdownOpen && (
                                        <div className="ac-model-dropdown">
                                            {(availableModels.length > 0 ? availableModels : [{
                                                id: form.model,
                                                display_name: getModelDisplayName(form.model),
                                                supports_temperature: 'yes',
                                                supports_reasoning: true,
                                            } as LLMModelInfo]).map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    className={`ac-model-option${form.model === opt.id ? ' selected' : ''}`}
                                                    data-value={opt.id}
                                                    onClick={handleModelSelect}
                                                    disabled={!canConfigureModelSettings}
                                                >
                                                    <div className="ac-model-option-row">
                                                        <div className="ac-model-option-title">{opt.display_name}</div>
                                                        {formatModelLimits(opt) && (
                                                            <div className="ac-model-option-inline-meta">{formatModelLimits(opt)}</div>
                                                        )}
                                                    </div>
                                                    <div className="ac-model-option-meta">{opt.id}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                </div>

                                <div className="ac-temp-section">
                                    <h3 className="ac-temp-title">Temperature 0.0 - 2.0</h3>
                                    <div className="ac-temp-card">
                                    {temperatureEnabled ? (
                                        <>
                                            <div className="ac-temp-slider-row">
                                                <input
                                                    ref={tempSliderRef}
                                                    type="range"
                                                    name="temperature"
                                                    min={0}
                                                    max={2}
                                                    step={0.1}
                                                    value={form.temperature}
                                                    onChange={handleFormChange}
                                                    disabled={loading || !canConfigureModelSettings}
                                                    className="ac-temp-slider"
                                                    aria-label="Temperature"
                                                />
                                                <input
                                                    type="number"
                                                    name="temperature"
                                                    value={form.temperature}
                                                    min={0}
                                                    max={2}
                                                    step={0.1}
                                                    onChange={handleFormChange}
                                                    disabled={loading || !canConfigureModelSettings}
                                                    className="ac-temp-number"
                                                    aria-label="Temperature value"
                                                />
                                            </div>
                                            <div className="ac-temp-footer">
                                                <span>Precise</span>
                                                {selectedModel && (
                                                    <div className="ac-model-meta ac-temp-meta">
                                                        <span className="ac-model-meta-item">Input {selectedModel.input_cost_per_million || '—'}/1M</span>
                                                        <span className="ac-model-meta-item">Output {selectedModel.output_cost_per_million || '—'}/1M</span>
                                                    </div>
                                                )}
                                                <span>Creative</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="ac-temp-unavailable">Not available for this model setup</div>
                                        </>
                                    )}
                                    {temperatureSupportMessage && (
                                        <p className="ac-control-note">{temperatureSupportMessage}</p>
                                    )}
                                    {unavailableModelNotice && (
                                        <p className="ac-control-note">{unavailableModelNotice}</p>
                                    )}
                                    {!isProviderAvailableInCurrentMode(form.provider) && (
                                        <p className="ac-control-note">{getProviderUnavailableReason(form.provider)}</p>
                                    )}
                                    {!canConfigureModelSettings && (
                                        <p className="ac-control-note">
                                            {form.subscription_mode === 'platform'
                                                ? 'These platform model settings are inherited from the super admin configuration.'
                                                : 'Model settings are not editable for your current role.'}
                                        </p>
                                    )}
                                    </div>
                                </div>
                            </>
                            )}
                        </section>

                        {supportsReasoning && reasoningOptions.length > 0 && (
                            <section className="ac-section">
                                <h2 className="ac-section-title">Reasoning Effort</h2>
                                <div className="ac-reasoning-card">
                                    <label className="ac-select-label" htmlFor="reasoning_effort">
                                        Choose how much deliberate reasoning the model should use for agent runs.
                                    </label>
                                    <div className="ac-reasoning-dropdown-wrap" ref={reasoningDropdownRef}>
                                        <button
                                            type="button"
                                            className="ac-reasoning-trigger"
                                            onClick={handleReasoningDropdownToggle}
                                            disabled={loading || !canConfigureModelSettings}
                                        >
                                            <span className="ac-reasoning-value">
                                                {effectiveReasoningEffort.charAt(0).toUpperCase() + effectiveReasoningEffort.slice(1)}
                                            </span>
                                            <ChevronDown className="ac-model-chevron" />
                                        </button>
                                        {reasoningDropdownOpen && (
                                            <div className="ac-model-dropdown ac-reasoning-options">
                                                {reasoningOptions.map(option => (
                                                    <button
                                                        key={option}
                                                        type="button"
                                                        className={`ac-model-option${effectiveReasoningEffort === option ? ' selected' : ''}`}
                                                        data-value={option}
                                                        onClick={handleReasoningSelect}
                                                        disabled={!canConfigureModelSettings}
                                                    >
                                                        <div className="ac-model-option-title">
                                                            {option.charAt(0).toUpperCase() + option.slice(1)}
                                                        </div>
                                                        {selectedModel?.reasoning_effort_default === option && (
                                                            <div className="ac-model-option-meta">Recommended default</div>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <p className="ac-control-note">
                                        {selectedModel?.reasoning_effort_default
                                            ? `Recommended default: ${selectedModel.reasoning_effort_default}.`
                                            : 'Use lower effort for speed and higher effort for harder problems.'}
                                    </p>
                                </div>
                            </section>
                        )}

                        <section className="ac-section">
                            <div className="ac-section-heading-row">
                                <h2 className="ac-section-title">Model Routing</h2>
                                {routesLoading && (
                                    <span className="ac-inline-loader">
                                        <Spinner animation="border" size="sm" />
                                        Loading
                                    </span>
                                )}
                            </div>
                            <p className="ac-info-note ac-route-info-note">
                                <InfoCircleFill className="ac-info-icon" />
                                Save the relevant provider key(s) from the top model selection section first in order to enable provider and model selection here.
                            </p>
                            <div
                                className="ac-route-table-wrap"
                                role="region"
                                aria-label="Model routing settings"
                                tabIndex={0}
                            >
                                <table className="ac-route-table" data-responsive-cards="true">
                                    <thead>
                                        <tr>
                                            <th>Job</th>
                                            <th>Use</th>
                                            <th>Provider</th>
                                            <th>Model</th>
                                            <th>Temp</th>
                                            <th>Reasoning</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {routeForms.map(route => {
                                            const modelOptions = getRouteModelOptions(route.provider);
                                            const selectedRouteModel = modelOptions.find(item => item.id === route.model) || null;
                                            const routeReasoningOptions = selectedRouteModel?.supports_reasoning && selectedRouteModel?.reasoning_effort_options?.length
                                                ? selectedRouteModel.reasoning_effort_options
                                                : [];
                                            const routeTemperatureEnabled = canAdjustTemperature(selectedRouteModel, route.reasoning_effort);
                                            const routeUnavailableNotice = getRouteUnavailableNotice(route, modelOptions);
                                            return (
                                                <tr key={route.route_key}>
                                                    <td data-label="Job">
                                                        <div className="ac-route-title">{route.label}</div>
                                                        <div className="ac-route-description">{route.description || route.route_key}</div>
                                                        {!route.enabled && route.is_inherited && (
                                                            <div className="ac-route-inherited">Inherited: {getModelDisplayName(route.effective_model, routeModels)}</div>
                                                        )}
                                                    </td>
                                                    <td data-label="Use">
                                                        <label className="ac-route-switch">
                                                            <input
                                                                type="checkbox"
                                                                data-route-key={route.route_key}
                                                                checked={route.enabled}
                                                                onChange={handleRouteEnabledChange}
                                                                disabled={!canConfigureRoutes || saving || routesLoading}
                                                            />
                                                            <span>{route.enabled ? 'Route' : 'Default'}</span>
                                                        </label>
                                                    </td>
                                                    <td data-label="Provider">
                                                        <div
                                                            className={`ac-reasoning-dropdown-wrap ac-route-dropdown-wrap${isRouteDropdownOpen(`${route.route_key}:provider`) ? ' is-open' : ''}`}
                                                            ref={(node) => { routeDropdownRefs.current[`${route.route_key}:provider`] = node; }}
                                                        >
                                                            <button
                                                                type="button"
                                                                className="ac-reasoning-trigger ac-route-dropdown-trigger"
                                                                onClick={() => handleRouteDropdownToggle(`${route.route_key}:provider`)}
                                                                disabled={!canConfigureRoutes || !route.enabled || saving || routesLoading}
                                                            >
                                                                <span className="ac-reasoning-value">{getProviderLabel(route.provider)}</span>
                                                                <ChevronDown className="ac-model-chevron" />
                                                            </button>
                                                            {isRouteDropdownOpen(`${route.route_key}:provider`) && (
                                                                <div className="ac-model-dropdown ac-reasoning-options ac-route-dropdown-menu">
                                                                    {PROVIDER_OPTIONS.map(option => {
                                                                        const available = isProviderAvailableInCurrentMode(option.value);
                                                                        const reason = getProviderUnavailableReason(option.value);
                                                                        return (
                                                                            <button
                                                                                key={option.value}
                                                                                type="button"
                                                                                className={`ac-model-option${route.provider === option.value ? ' selected' : ''}`}
                                                                                onClick={() => handleRouteProviderSelect(route.route_key, option.value)}
                                                                                disabled={!canConfigureRoutes || !route.enabled || saving || routesLoading || !available}
                                                                            >
                                                                                <div className="ac-model-option-title">{option.label}</div>
                                                                                {!available && (
                                                                                    <div className="ac-model-option-meta">{reason || 'Unavailable'}</div>
                                                                                )}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td data-label="Model">
                                                        <div
                                                            className={`ac-reasoning-dropdown-wrap ac-route-dropdown-wrap${isRouteDropdownOpen(`${route.route_key}:model`) ? ' is-open' : ''}`}
                                                            ref={(node) => { routeDropdownRefs.current[`${route.route_key}:model`] = node; }}
                                                        >
                                                            <button
                                                                type="button"
                                                                className="ac-reasoning-trigger ac-route-dropdown-trigger"
                                                                onClick={() => handleRouteDropdownToggle(`${route.route_key}:model`)}
                                                                disabled={!canConfigureRoutes || !route.enabled || saving || routesLoading}
                                                            >
                                                                <span className="ac-reasoning-value">{getModelDisplayName(route.model, modelOptions)}</span>
                                                                <ChevronDown className="ac-model-chevron" />
                                                            </button>
                                                            {isRouteDropdownOpen(`${route.route_key}:model`) && (
                                                                <div className="ac-model-dropdown ac-reasoning-options ac-route-dropdown-menu">
                                                                    {(modelOptions.length > 0 ? modelOptions : [{ id: route.model, display_name: route.model } as LLMModelInfo]).map(option => (
                                                                        <button
                                                                            key={option.id}
                                                                            type="button"
                                                                            className={`ac-model-option${route.model === option.id ? ' selected' : ''}`}
                                                                            onClick={() => handleRouteModelDropdownSelect(route.route_key, option.id)}
                                                                            disabled={!canConfigureRoutes || !route.enabled || saving || routesLoading}
                                                                        >
                                                                            <div className="ac-model-option-title">{option.display_name || option.id}</div>
                                                                            <div className="ac-model-option-meta">{option.id}</div>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td data-label="Temperature">
                                                        {routeTemperatureEnabled ? (
                                                            <input
                                                                type="number"
                                                                className="ac-route-temp"
                                                                value={route.temperature}
                                                                min={0}
                                                                max={2}
                                                                step={0.1}
                                                                data-route-key={route.route_key}
                                                                onChange={handleRouteTemperatureChange}
                                                                disabled={!canConfigureRoutes || !route.enabled || saving || routesLoading}
                                                            />
                                                        ) : (
                                                            <span className="ac-route-description">N/A</span>
                                                        )}
                                                    </td>
                                                    <td data-label="Reasoning">
                                                        {routeReasoningOptions.length > 0 ? (
                                                            <div
                                                                className={`ac-reasoning-dropdown-wrap ac-route-dropdown-wrap${isRouteDropdownOpen(`${route.route_key}:reasoning`) ? ' is-open' : ''}`}
                                                                ref={(node) => { routeDropdownRefs.current[`${route.route_key}:reasoning`] = node; }}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="ac-reasoning-trigger ac-route-dropdown-trigger"
                                                                    onClick={() => handleRouteDropdownToggle(`${route.route_key}:reasoning`)}
                                                                    disabled={!canConfigureRoutes || !route.enabled || saving || routesLoading}
                                                                >
                                                                    <span className="ac-reasoning-value">
                                                                        {route.reasoning_effort.charAt(0).toUpperCase() + route.reasoning_effort.slice(1)}
                                                                    </span>
                                                                    <ChevronDown className="ac-model-chevron" />
                                                                </button>
                                                                {isRouteDropdownOpen(`${route.route_key}:reasoning`) && (
                                                                    <div className="ac-model-dropdown ac-reasoning-options ac-route-dropdown-menu">
                                                                        {routeReasoningOptions.map(option => (
                                                                            <button
                                                                                key={option}
                                                                                type="button"
                                                                                className={`ac-model-option${route.reasoning_effort === option ? ' selected' : ''}`}
                                                                                onClick={() => handleRouteReasoningDropdownSelect(route.route_key, option)}
                                                                                disabled={!canConfigureRoutes || !route.enabled || saving || routesLoading}
                                                                            >
                                                                                <div className="ac-model-option-title">
                                                                                    {option.charAt(0).toUpperCase() + option.slice(1)}
                                                                                </div>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="ac-route-description">N/A</span>
                                                        )}
                                                        {routeUnavailableNotice && (
                                                            <div className="ac-route-description">{routeUnavailableNotice}</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {!canConfigureRoutes && (
                                <p className="ac-control-note">
                                    {form.subscription_mode === 'platform'
                                        ? 'Model routes are inherited from the platform configuration.'
                                        : 'Model routes are not editable for your current role.'}
                                </p>
                            )}
                        </section>
                        </>
                        ) : (
                        <section className="ac-section">
                            <h2 className="ac-section-title">Inherited Platform Configuration</h2>
                            <p className="ac-control-note">
                                In Platform Subscription mode, provider keys, model selection, reasoning effort, and model routing are inherited from the super admin configuration.
                            </p>
                            <p className="ac-control-note">
                                Select <strong>Use My Own API Key / Subscription</strong> to configure organization-specific keys, models, and routing.
                            </p>
                        </section>
                        )}

                        {/* Form footer */}
                        <div className="ac-form-footer">
                            <button
                                type="button"
                                className="ac-reset-btn"
                                onClick={handleLlmResetClick}
                                disabled={saving || loading}
                            >
                                Reset
                            </button>
                            <button
                                type="submit"
                                className="ac-primary-save-btn"
                                disabled={saving || loading}
                            >
                                {saving ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                        {' Saving...'}
                                    </>
                                ) : 'Save Changes'}
                            </button>
                        </div>
                    </form>

                    </>
                )}

                {/* ── Stripe & LLM Usage Credits ── */}
                {activeTab === 'billing' && isSuperAdmin && (
                    <form className="ac-llm-content" onSubmit={handleBillingSubmit}>
                        <section className="ac-section ac-stripe-section">
                            <h2 className="ac-section-title">Stripe &amp; LLM Usage Credits</h2>
                            <div className="ac-stripe-layout">
                                <div className="ac-stripe-panel">
                                    <div className="ac-stripe-panel-header">
                                        <div>
                                            <h3 className="ac-stripe-panel-title">Billing Credentials</h3>
                                            <p className="ac-stripe-panel-copy">
                                                Platform-wide keys used for checkout, invoices, and customer billing flows.
                                            </p>
                                        </div>
                                        <span
                                            className={`ac-stripe-status${stripeSecretKeyConfigured && stripeWebhookSecretConfigured ? ' is-configured' : ' is-missing'}`}
                                        >
                                            {stripeSecretKeyConfigured && stripeWebhookSecretConfigured ? 'Stripe secrets saved' : 'Stripe secrets missing'}
                                        </span>
                                    </div>

                                    <div className="ac-stripe-field-stack">
                                        <div className="ac-stripe-field">
                                            <label className="ac-select-label" htmlFor="stripe_secret_key">
                                                Stripe Secret Key
                                            </label>
                                            <div className="ac-api-key-group">
                                                <input
                                                    id="stripe_secret_key"
                                                    type={stripeSecretVisible ? 'text' : 'password'}
                                                    className="ac-api-key-input"
                                                    value={stripeSecretKey}
                                                    onChange={(e) => {
                                                        setStripeSecretKey(e.target.value);
                                                        setStripeSecretKeyTouched(true);
                                                    }}
                                                    disabled={saving}
                                                    placeholder="Enter Stripe secret key"
                                                    autoComplete="off"
                                                />
                                                <button
                                                    type="button"
                                                    className="ac-hide-btn"
                                                    onClick={() => setStripeSecretVisible(v => !v)}
                                                    disabled={saving}
                                                >
                                                    {stripeSecretVisible ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                            <p className="ac-control-note ac-stripe-field-note">
                                                {stripeSecretKeyConfigured
                                                    ? 'This field shows a masked preview of the current Stripe secret key. Edit it to replace it, or clear it and save to remove the stored value.'
                                                    : 'No Stripe secret key is configured yet.'}
                                            </p>
                                        </div>

                                        <div className="ac-stripe-field">
                                            <label className="ac-select-label" htmlFor="stripe_webhook_secret">
                                                Stripe Webhook Secret
                                            </label>
                                            <div className="ac-api-key-group">
                                                <input
                                                    id="stripe_webhook_secret"
                                                    type={stripeWebhookSecretVisible ? 'text' : 'password'}
                                                    className="ac-api-key-input"
                                                    value={stripeWebhookSecret}
                                                    onChange={(e) => {
                                                        setStripeWebhookSecret(e.target.value);
                                                        setStripeWebhookSecretTouched(true);
                                                    }}
                                                    disabled={saving}
                                                    placeholder="Enter Stripe webhook signing secret"
                                                    autoComplete="off"
                                                />
                                                <button
                                                    type="button"
                                                    className="ac-hide-btn"
                                                    onClick={() => setStripeWebhookSecretVisible(v => !v)}
                                                    disabled={saving}
                                                >
                                                    {stripeWebhookSecretVisible ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                            <p className="ac-control-note ac-stripe-field-note">
                                                {stripeWebhookSecretConfigured
                                                    ? 'This field shows a masked preview of the current Stripe webhook signing secret. Edit it to replace it, or clear it and save to remove the stored value.'
                                                    : 'No Stripe webhook signing secret is configured yet.'}
                                            </p>
                                        </div>

                                        <div className="ac-stripe-field">
                                            <label className="ac-select-label" htmlFor="stripe_publishable_key">
                                                Stripe Publishable Key
                                            </label>
                                            <div className="ac-api-key-group">
                                                <input
                                                    id="stripe_publishable_key"
                                                    type={stripePublishableVisible ? 'text' : 'password'}
                                                    className="ac-api-key-input"
                                                    value={stripePublishableKey}
                                                    onChange={(e) => setStripePublishableKey(e.target.value)}
                                                    disabled={saving}
                                                    placeholder="Enter Stripe publishable key"
                                                    autoComplete="off"
                                                />
                                                <button
                                                    type="button"
                                                    className="ac-hide-btn"
                                                    onClick={() => setStripePublishableVisible(v => !v)}
                                                    disabled={saving}
                                                >
                                                    {stripePublishableVisible ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                            <p className="ac-control-note ac-stripe-field-note">
                                                {stripePublishableKey
                                                    ? 'This field shows a masked preview of the current Stripe publishable key. Edit it to replace it, or clear it and save to remove the stored value.'
                                                    : 'No Stripe publishable key is configured yet.'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="ac-stripe-panel">
                                    <div className="ac-stripe-panel-header">
                                        <div>
                                            <h3 className="ac-stripe-panel-title">LLM Usage Credits</h3>
                                            <p className="ac-stripe-panel-copy">
                                                Configure funded credits for platform-key LLM usage and the margin applied to provider cost.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="ac-stripe-field">
                                        <label className="ac-select-label" htmlFor="llm_token_profit_percentage">
                                            LLM Token Profit Percentage
                                        </label>
                                        <div className="ac-stripe-profit-input-wrap">
                                            <input
                                                id="llm_token_profit_percentage"
                                                type="number"
                                                min={0}
                                                max={100}
                                                step={0.01}
                                                className="ac-api-key-input ac-standalone-input ac-stripe-profit-input"
                                                value={llmTokenProfitPercentage}
                                                onChange={handleProfitPercentageChange}
                                                disabled={saving}
                                                placeholder="0"
                                            />
                                            <span className="ac-stripe-profit-suffix">%</span>
                                        </div>
                                        <p className="ac-control-note ac-stripe-field-note">
                                            Use 0 to price at raw provider cost, or increase this value to apply a margin.
                                        </p>
                                    </div>

                                    <div className="ac-stripe-field">
                                        <label className="ac-select-label" htmlFor="llm_usage_recharge_amount_usd">
                                            LLM Usage Credits Recharge Amount
                                        </label>
                                        <div className="ac-stripe-profit-input-wrap">
                                            <input
                                                id="llm_usage_recharge_amount_usd"
                                                type="number"
                                                min={0.01}
                                                step={0.01}
                                                className="ac-api-key-input ac-standalone-input ac-stripe-profit-input"
                                                value={llmUsageRechargeAmountUsd}
                                                onChange={(e) => handleUsdInputChange(e, setLlmUsageRechargeAmountUsd)}
                                                disabled={saving}
                                                placeholder="50.00"
                                            />
                                            <span className="ac-stripe-profit-suffix">USD</span>
                                        </div>
                                        <p className="ac-control-note ac-stripe-field-note">
                                            Charged as a separate Stripe receipt when initial, manual, or automatic credits are added.
                                        </p>
                                    </div>

                                    <div className="ac-stripe-field">
                                        <label className="ac-select-label" htmlFor="llm_usage_auto_recharge_threshold_usd">
                                            Auto-Recharge Threshold
                                        </label>
                                        <div className="ac-stripe-profit-input-wrap">
                                            <input
                                                id="llm_usage_auto_recharge_threshold_usd"
                                                type="number"
                                                min={0}
                                                step={0.01}
                                                className="ac-api-key-input ac-standalone-input ac-stripe-profit-input"
                                                value={llmUsageAutoRechargeThresholdUsd}
                                                onChange={(e) => handleUsdInputChange(e, setLlmUsageAutoRechargeThresholdUsd)}
                                                disabled={saving}
                                                placeholder="5.00"
                                            />
                                            <span className="ac-stripe-profit-suffix">USD</span>
                                        </div>
                                        <p className="ac-control-note ac-stripe-field-note">
                                            When a platform-key organization balance reaches this amount, AcceliQA attempts an automatic recharge.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <p className="ac-info-note ac-stripe-info-note">
                                <InfoCircleFill className="ac-info-icon" />
                                Stripe keys and credit recharge settings are platform-wide. In BYO mode, regular LLM calls use the organization's own provider key while vector-ingestion and embedding calls still use LLM Usage Credits.
                            </p>
                        </section>

                        <div className="ac-form-footer">
                            <button
                                type="button"
                                className="ac-reset-btn"
                                onClick={handleBillingResetClick}
                                disabled={saving || loading}
                            >
                                Reset
                            </button>
                            <button
                                type="submit"
                                className="ac-primary-save-btn"
                                disabled={saving || loading}
                            >
                                {saving ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                        {' Saving...'}
                                    </>
                                ) : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                )}

                {/* ── Prompt Configuration ── */}
                {activeTab === 'prompts' && isSuperAdmin && (
                    <div className="ac-prompts-content">
                        {/* Sidebar */}
                        <aside className="ac-prompt-sidebar">
                            {promptTypeOrder.length === 0 && (
                                <p className="ac-sidebar-empty">No agents</p>
                            )}
                            {promptTypeOrder.map(typeKey => (
                                <button
                                    key={typeKey}
                                    type="button"
                                    data-type={typeKey}
                                    className={`ac-sidebar-item${activePromptType === typeKey ? ' active' : ''}`}
                                    onClick={handlePromptTypeClick}
                                >
                                    {getPromptTypeLabel(typeKey)}
                                </button>
                            ))}
                        </aside>

                        {/* Main area */}
                        <div className="ac-prompt-main">
                            {promptTypeOrder.length === 0 && (
                                <p className="ac-prompt-empty">No prompts found. Add entries via API.</p>
                            )}
                            {promptTypeOrder.length > 0 && (
                                <>
                                    <h2 className="ac-prompt-section-title">
                                        {getPromptTypeLabel(activePromptType)}
                                    </h2>
                                    {buildPromptCards(
                                        prompts,
                                        activePromptType,
                                        promptSaving,
                                        handlePromptContentChangeEvent,
                                        handlePromptSaveClick,
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Org Prompt Instructions (org-admin only) ── */}
                {activeTab === 'org-prompts' && !isSuperAdmin && (() => {
                    const orgGroups: Record<string, OrgPromptInstructionItem[]> = {};
                    for (const item of orgInstructions) {
                        const t = item.prompt_type || PROMPT_TYPE_DEFAULT;
                        if (!orgGroups[t]) orgGroups[t] = [];
                        orgGroups[t].push(item);
                    }
                    const orgTypeOrder = Object.keys(orgGroups).sort();
                    return (
                        <div className="ac-prompts-content">
                            {/* Sidebar */}
                            <aside className="ac-prompt-sidebar">
                                {orgInstructionsLoading && <p className="ac-sidebar-empty">Loading…</p>}
                                {!orgInstructionsLoading && orgTypeOrder.length === 0 && (
                                    <p className="ac-sidebar-empty">No agents</p>
                                )}
                                {orgTypeOrder.map(typeKey => (
                                    <button
                                        key={typeKey}
                                        type="button"
                                        data-type={typeKey}
                                        className={`ac-sidebar-item${orgActiveType === typeKey ? ' active' : ''}`}
                                        onClick={handleOrgTypeClick}
                                    >
                                        {getPromptTypeLabel(typeKey)}
                                    </button>
                                ))}
                            </aside>

                            {/* Main area */}
                            <div className="ac-prompt-main">
                                {/* User guidelines collapsible */}
                                <div className="ac-org-guidelines">
                                    <button
                                        type="button"
                                        className="ac-org-guidelines-toggle"
                                        onClick={() => setOrgGuidelinesOpen(o => !o)}
                                    >
                                        <InfoCircleFill className="ac-info-icon" />
                                        {orgGuidelinesOpen ? 'Hide' : 'Show'} writing guidelines
                                        <ChevronDown
                                            className="ac-model-chevron"
                                            style={{ transform: orgGuidelinesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                        />
                                    </button>
                                    {orgGuidelinesOpen && (
                                        <div className="ac-org-guidelines-body">
                                            <p className="ac-org-guidelines-intro">
                                                Custom instructions are appended to the base agent prompt at runtime.
                                                They are <strong>supplementary</strong> and must not attempt to change the agent's output format.
                                            </p>
                                            <div className="ac-org-guidelines-cols">
                                                <div>
                                                    <p className="ac-org-guidelines-section-label ac-guidelines-do">✓ Do</p>
                                                    <ul className="ac-org-guidelines-list">
                                                        <li>Be specific and actionable: <em>"Always include a test case for API timeout scenarios."</em></li>
                                                        <li>Use imperative language: <em>Always…, Never…, Must…, For every…</em></li>
                                                        <li>One instruction per line for clarity.</li>
                                                        <li>Reference your domain names: <em>"Our payment module is called PayCore."</em></li>
                                                        <li>Good: <em>"Always include at least one test case for null/empty input per user story."</em></li>
                                                        <li>Good: <em>"Use our naming convention: [ServiceName]-[Scenario] for all test case titles."</em></li>
                                                    </ul>
                                                </div>
                                                <div>
                                                    <p className="ac-org-guidelines-section-label ac-guidelines-dont">✗ Don't</p>
                                                    <ul className="ac-org-guidelines-list">
                                                        <li>Don't try to change the output format — field names and JSON structure are fixed.</li>
                                                        <li>Don't duplicate existing rules — only add what's new.</li>
                                                        <li>Avoid vague qualifiers: <em>"better", "more", "improved"</em> without measurable criteria.</li>
                                                        <li>Bad: <em>"Make test cases more comprehensive."</em></li>
                                                        <li>Bad: <em>"Use good naming."</em></li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {orgInstructionsLoading && (
                                    <div className="ac-dependent-loader">
                                        <Spinner animation="border" size="sm" />
                                        <span>Loading…</span>
                                    </div>
                                )}
                                {!orgInstructionsLoading && orgTypeOrder.length === 0 && (
                                    <p className="ac-prompt-empty">No agents found.</p>
                                )}
                                {!orgInstructionsLoading && orgTypeOrder.length > 0 && (orgGroups[orgActiveType] || []).map(item => (
                                    <div className="ac-prompt-card" key={item.agent_name}>
                                        <div className="ac-prompt-card-header">
                                            <div className="ac-prompt-card-meta">
                                                <h3 className="ac-prompt-agent-name">{item.agent_name}</h3>
                                                <p className="ac-prompt-description">
                                                    Add supplementary instructions for this agent. Leave blank to use defaults.
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                className="ac-save-btn"
                                                data-agent-name={item.agent_name}
                                                disabled={orgInstructionsSaving !== null}
                                                onClick={handleOrgInstructionSave}
                                            >
                                                {orgInstructionsSaving === item.agent_name ? 'Saving…' : 'Save'}
                                            </button>
                                        </div>
                                        <textarea
                                            className="ac-prompt-textarea"
                                            rows={8}
                                            maxLength={300}
                                            data-agent-name={item.agent_name}
                                            value={item.custom_instructions ?? ''}
                                            onChange={handleOrgInstructionChange}
                                            placeholder={`Add custom instructions for ${item.agent_name}…\n\nExample:\n• Always include a test case for null/empty input validation.\n• Use our naming convention: [ServiceName]-[Scenario].`}
                                            aria-label={`Custom instructions for ${item.agent_name}`}
                                        />
                                        <div className={`ac-char-counter${(item.custom_instructions ?? '').length >= 300 ? ' at-limit' : ''}`}>
                                            {(item.custom_instructions ?? '').length} / 300
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
}

export default AdminConfigNew;
